/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import * as _ from 'lodash';
import template from './blocklist-edit-page.component.html';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {normalizeIabCategoryIds} from '../../components/util/iab-category-search';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {BlocklistApiService, BlocklistModel} from './blocklist-api.service';
import {FormsModule} from '@angular/forms';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {APP_SHARED} from '../common/app-shared';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {BlocklistEditUsageComponent} from './blocklist-edit-usage.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, ButtonsModule, AdnSelectComponent, ...FORM_CONTROLS, BlocklistEditUsageComponent],
  selector: 'adn-blocklist-edit-page',
  template
})
export class BlocklistEditPageComponent extends SignalAdminResourceEditPageBase<BlocklistModel> {
  private readonly allTeamsState = signal<any[] | null>(null);
  private readonly advertiserSearchResultsState = signal<any[]>([]);
  private readonly lineItemSearchResultsState = signal<any[]>([]);
  private readonly creativeSearchResultsState = signal<any[]>([]);
  get allTeams(): any[] | null { return this.allTeamsState(); }
  set allTeams(value: any[] | null) { this.allTeamsState.set(value); }
  get advertiserSearchResults(): any[] { return this.advertiserSearchResultsState(); }
  set advertiserSearchResults(value: any[]) { this.advertiserSearchResultsState.set(value); }
  get lineItemSearchResults(): any[] { return this.lineItemSearchResultsState(); }
  set lineItemSearchResults(value: any[]) { this.lineItemSearchResultsState.set(value); }
  get creativeSearchResults(): any[] { return this.creativeSearchResultsState(); }
  set creativeSearchResults(value: any[]) { this.creativeSearchResultsState.set(value); }
  readonly teamsLoading = signal(true);
  readonly teamsLoadFailed = signal(false);
  canUpdateBlocklist = false;

  private readonly blocklistApi = inject(BlocklistApiService);
  private readonly blocklistDestroyRef = inject(DestroyRef);

  constructor(@Inject(LocalUserPermissions) LocalUserPermissions: any) {
    super(inject(BlocklistApiService), 'blocklist');
    this.canUpdateBlocklist = LocalUserPermissions.hasAny('AX_PUBLISHER') || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');

    this.blocklistApi.listTeams().pipe(
      finalize(() => this.teamsLoading.set(false)),
      takeUntilDestroyed(this.blocklistDestroyRef)
    ).subscribe({
      next: (page) => {
        this.allTeams = _.filter(page.results, (team: any) => {
          return team.type === 'STANDARD' || team.type === 'AX_PUBLISHER';
        });
        this.resolveTeamReference();
      },
      error: () => {
        this.allTeams = [];
        this.teamsLoadFailed.set(true);
      }
    });
  }

  protected override initialiseModel(model: BlocklistModel): BlocklistModel {
    const initialised = super.initialiseModel(model);
    initialised.iabCategoryAction = initialised.iabCategoryAction || 'BLOCKED';
    initialised.iabCategories = normalizeIabCategoryIds(initialised.iabCategories);
    initialised.advertiserDomains = _.map(initialised.advertiserDomains || [], (domain: any) => {
      return _.get(domain, 'text', domain);
    });
    this.resolveTeamReference(initialised);
    return initialised;
  }

  protected override afterSave(model: BlocklistModel): void {
    model.team = model.team || undefined;
    model.iabCategoryAction = model.iabCategoryAction || 'BLOCKED';
    this.resolveTeamReference(model);
  }

  protected override prepareForSubmit(model: BlocklistModel): void {
    model.advertiserDomains = _.map(model.advertiserDomains || [], (domain: any) => {
      return _.get(domain, 'text', domain);
    });
    model.iabCategories = normalizeIabCategoryIds(model.iabCategories);
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.search('Advertiser', searchElement.search, (results) => this.advertiserSearchResults = results);
  };

  lineItemSearch = (searchElement: AdnSelectSearchElement): void => {
    this.search('LineItem', searchElement.search, (results) => this.lineItemSearchResults = results);
  };

  creativeSearch = (searchElement: AdnSelectSearchElement): void => {
    this.search('Creative', searchElement.search, (results) => this.creativeSearchResults = results);
  };

  private search(
    type: 'Advertiser' | 'LineItem' | 'Creative',
    query: string,
    apply: (results: any[]) => void
  ): void {
    this.blocklistApi.search(query, type).pipe(
      takeUntilDestroyed(this.blocklistDestroyRef)
    ).subscribe({
      next: (results) => {
        apply(results);
      },
      error: () => {
        apply([]);
      }
    });
  }

  private resolveTeamReference(model: BlocklistModel = this.editingModel): void {
    if (!model?.team || !this.allTeams?.length) {
      return;
    }
    const teamId = _.isString(model.team) ? model.team : model.team.id;
    const match = _.find(this.allTeams, ['id', teamId]);
    if (match) {
      model.team = match;
    }
  }
}
